// Six-axis radar (hexagon) for the personalization sliders. Pure
// presentational — takes the six normalized [0, 1] values and draws an
// SVG polygon that morphs on every render. The Settings drawer wires
// this directly to RankParams so dragging a slider re-shapes the
// polygon on the same frame.
//
// Axes are ordered clockwise from the top:
//   Match · Freshness · Trending · Authority · Variety · Discovery
//
// The polygon is filled translucent + outlined for legibility against
// the drawer's body background; concentric grid hexagons give a sense
// of scale (25%, 50%, 75%, 100%).

import type { RankParams } from "../feed/rank";

import { RANK_PREFS_BOUNDS } from "./rankPrefs";

/** Per-axis label + reader from RankParams. Order = clockwise from top.
 *
 *  Normalization note: each axis divides by its OWN slider bound
 *  (`RANK_PREFS_BOUNDS.<key>.max`), not by a global "contribution to
 *  score" magnitude. So `lambda=1.0` (with max=2) fills HALF the
 *  Match axis, while `recencyWeight=1.0` (with max=1) fills the
 *  ENTIRE Freshness axis. That's deliberate: the radar shows how
 *  far the user has pushed each slider within its own range, not
 *  how much each axis weighs in the final score. This matches the
 *  "your profile within the available range" reading the radar is
 *  supposed to convey. */
export const RADAR_AXES: ReadonlyArray<{
  key: keyof RankParams;
  label: string;
  read: (p: RankParams) => number;
}> = [
  {
    key: "lambda",
    label: "Match",
    read: (p) => p.lambda / RANK_PREFS_BOUNDS.lambda.max,
  },
  {
    key: "recencyWeight",
    label: "Freshness",
    read: (p) => p.recencyWeight / RANK_PREFS_BOUNDS.recencyWeight.max,
  },
  {
    key: "velocityWeight",
    label: "Trending",
    read: (p) => p.velocityWeight / RANK_PREFS_BOUNDS.velocityWeight.max,
  },
  {
    key: "authorityWeight",
    label: "Authority",
    read: (p) => p.authorityWeight / RANK_PREFS_BOUNDS.authorityWeight.max,
  },
  {
    key: "publisherDiversity",
    label: "Variety",
    read: (p) =>
      p.publisherDiversity / RANK_PREFS_BOUNDS.publisherDiversity.max,
  },
  {
    key: "topicDiversity",
    label: "Discovery",
    read: (p) => p.topicDiversity / RANK_PREFS_BOUNDS.topicDiversity.max,
  },
] as const;

export interface RadarProps {
  params: RankParams;
}

/* The viewBox is WIDER than it is tall, and the radar geometry is a
 * smaller radius centered inside it, so the side labels ("Discovery",
 * "Freshness", "Trending", "Variety") have horizontal room and don't
 * clip at the edges. Top/bottom labels are middle-anchored and need
 * less room, so the vertical margin is tighter. */
const VIEWBOX_W = 320;
const VIEWBOX_H = 232;
const CENTER_X = VIEWBOX_W / 2;
const CENTER_Y = VIEWBOX_H / 2;
const RADIUS = 76;
const LABEL_OFFSET = 16;

/** Map (axis index, normalized 0-1 magnitude) → an (x, y) on the
 *  drawing plane. Angle 0 is at the top (12 o'clock), increases
 *  clockwise — matches the way the human eye reads "first axis at the
 *  top, then around to the right." */
function axisPoint(i: number, mag: number): [number, number] {
  const theta = -Math.PI / 2 + (2 * Math.PI * i) / RADAR_AXES.length;
  const r = RADIUS * Math.max(0, Math.min(1, mag));
  return [CENTER_X + r * Math.cos(theta), CENTER_Y + r * Math.sin(theta)];
}

function ringPath(scale: number): string {
  const pts = RADAR_AXES.map((_, i) => axisPoint(i, scale));
  return (
    pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join("") +
    "Z"
  );
}

export function Radar({ params }: RadarProps) {
  const values = RADAR_AXES.map((a) => a.read(params));
  const polygon = values
    .map((v, i) => {
      const [x, y] = axisPoint(i, v);
      return i === 0 ? `M${x},${y}` : `L${x},${y}`;
    })
    .join("") + "Z";

  return (
    <div class="radar-wrap" role="img" aria-label="Personalization profile radar">
      <svg
        class="radar"
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Concentric guide rings at 25, 50, 75, 100%. Drawn first so the
          * shape polygon overlays them. */}
        {[0.25, 0.5, 0.75, 1].map((s) => (
          <path key={s} class="radar-ring" d={ringPath(s)} />
        ))}
        {/* Axis spokes — line from center to the outer ring on each axis. */}
        {RADAR_AXES.map((_, i) => {
          const [x, y] = axisPoint(i, 1);
          return (
            <line
              key={i}
              class="radar-spoke"
              x1={CENTER_X}
              y1={CENTER_Y}
              x2={x}
              y2={y}
            />
          );
        })}
        {/* The user's profile polygon. Filled + outlined. CSS handles
          * the transition so the morph between slider positions is
          * visibly animated. */}
        <path class="radar-shape" d={polygon} />
        {/* Anchor dots at each axis vertex so a near-zero value still
          * reads visually. */}
        {values.map((v, i) => {
          const [x, y] = axisPoint(i, v);
          return <circle key={i} class="radar-dot" cx={x} cy={y} r={3} />;
        })}
        {/* Labels sitting just outside the outer ring. */}
        {RADAR_AXES.map((a, i) => {
          const [x, y] = axisPoint(i, 1 + LABEL_OFFSET / RADIUS);
          // Anchor based on horizontal position so labels read
          // "outward" cleanly on left/right sides.
          const anchor =
            x < CENTER_X - 4 ? "end" : x > CENTER_X + 4 ? "start" : "middle";
          return (
            <text
              key={a.key}
              class="radar-label"
              x={x}
              y={y}
              text-anchor={anchor}
              dominant-baseline="middle"
            >
              {a.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
